import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import WarningNoticeBanner from '../../../../../../components/designe/warning-notice-banner/WarningNoticeBanner';
import UserMultiSelectDialog from '../../../../../../components/reusable/user-multi-select-dialog/UserMultiSelectDialog';
import { useShareProgramItem } from '../../../../../../redux/api/programsAPI';
import { useGetUserTrainingAccessIncoming } from '../../../../../../redux/api/userTrainingAccessApi';
import { useGetMyDetails } from '../../../../../../redux/api/users/usersApi';
import { IProgramItem } from '../../../../../../types/interfaces/program-item';
import { accessRowToIUser } from '../../../../../users/settings/access-settings/access-settings-helpers';

const emptySelectedUserIds: string[] = [];

interface IShareProgramProps {
  open: boolean;
  programItem: IProgramItem;
  onClose: () => void;
  onShared?: () => void;
}

export default function ShareProgram(props: IShareProgramProps) {
  const { open, programItem, onClose, onShared } = props;
  const { t } = useTranslation();
  const { data: user } = useGetMyDetails();
  const {
    data: userTrainingAccess,
    isLoading: isAccessLoading,
    isFetching: isAccessFetching,
  } = useGetUserTrainingAccessIncoming();
  const [shareProgramItem] = useShareProgramItem();
  const [isSaving, setIsSaving] = useState(false);

  const shareProgramAllowedUsers = useMemo(
    () =>
      (userTrainingAccess?.sharePrograms ?? []).map((row) =>
        accessRowToIUser(row),
      ),
    [userTrainingAccess?.sharePrograms],
  );

  const alreadySharedIds = useMemo(
    () => new Set((programItem.availableToUsers ?? []).map((u) => u.id)),
    [programItem.availableToUsers],
  );

  const usersToPickFrom = useMemo(() => {
    if (!shareProgramAllowedUsers.length) {
      return [];
    }
    return shareProgramAllowedUsers.filter(
      (s) => s.id !== user?.id && !alreadySharedIds.has(s.id),
    );
  }, [shareProgramAllowedUsers, user?.id, alreadySharedIds]);
  const showEmptyStateWarning =
    !isAccessLoading && !isAccessFetching && usersToPickFrom.length === 0;

  const handleSave = async (add: string[]) => {
    if (!add.length) {
      return;
    }
    setIsSaving(true);
    try {
      await Promise.all(
        add.map((userId) =>
          shareProgramItem({
            itemId: programItem.id,
            userId,
          }).unwrap(),
        ),
      );
      onShared?.();
      onClose();
    } catch {
      // Error toast from RTK transform
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <UserMultiSelectDialog
      open={open}
      onClose={onClose}
      title={t('programs.shareProgram')}
      users={usersToPickFrom}
      selectedUserIds={emptySelectedUserIds}
      onSave={handleSave}
      isSaving={isSaving}
      requireSelection
      emptyStateNotice={
        showEmptyStateWarning ? (
          <WarningNoticeBanner title={t('programs.shareEmptyBannerTitle')}>
            {t('programs.shareEmptyBannerDescription')}
          </WarningNoticeBanner>
        ) : null
      }
    />
  );
}
